import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { getPendingActions, getPendingActionTypes, getPendingActionsSummary, getWarehouses } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const PendingActions = () => {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pendingActions, setPendingActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [selectedActionType, setSelectedActionType] = useState(null);
  const [summary, setSummary] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  useEffect(() => {
    loadWarehouses();
    loadPendingActions();
    loadActionTypes();
    loadSummary();
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        const warehouseOptions = response.data.map(w => ({
          value: w.id,
          label: w.name
        }));
        setWarehouses(warehouseOptions);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadPendingActions = async (filters = {}) => {
    setLoading(true);
    try {
      console.log(filters);
      const data = await getPendingActions(filters) || {};
      setPendingActions(data.data || []);
    } catch (error) {
      console.error('Error loading pending actions:', error);
      toast.error('Failed to load pending actions');
    } finally {
      setLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const data = await getPendingActionTypes() || {};
      const types = (data.data || []).map(type => ({
        value: type,
        label: type
      }));
      setActionTypes(types);
    } catch (error) {
      console.error('Error loading action types:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await getPendingActionsSummary() || {};
      setSummary(data.data || []);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleSearch = (value) => {
    setFilterText(value);
    const filters = {};
    if (value) filters.order_id = value;
    if (selectedActionType) filters.action_type = selectedActionType.value;
    if (selectedWarehouse) filters.warehouse_id = selectedWarehouse.value;
    loadPendingActions(filters);
  };

  const handleActionTypeChange = (selected) => {
    setSelectedActionType(selected);
    const filters = {};
    if (filterText) filters.order_id = filterText;
    if (selected) filters.action_type = selected.value;
    if (selectedWarehouse) filters.warehouse_id = selectedWarehouse.value;
    loadPendingActions(filters);
  };

  const handleWarehouseChange = (selected) => {
    console.log(selected);
    setSelectedWarehouse(selected);
    const filters = {};
    if (filterText) filters.order_id = filterText;
    if (selectedActionType) filters.action_type = selectedActionType.value;
    if (selected) filters.warehouse_id = selected.value;
    loadPendingActions(filters);
  };

  const handleClearFilters = () => {
    setFilterText('');
    setSelectedActionType(null);
    setSelectedWarehouse(null);
    loadPendingActions();
  };

  const columns = [
    {
      name: 'Order ID',
      selector: row => row.order_id,
      sortable: true,
      cell: row => (
        <Link 
          to={`/orders/${row.order_id}`}
          target={isMobile ? undefined : '_blank'}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div style={{ fontWeight: '600', color: '#0d6efd' }}>{row.order_id}</div>
        </Link>
      ),
      width: '150px'
    },
    {
      name: 'Warehouse',
      selector: row => row.warehouse_id,
      sortable: true,
      cell: row => {
        const warehouse = warehouses.find(w => w.value === row.warehouse_id);
        return <span>{warehouse ? warehouse.label : 'N/A'}</span>;
      },
      width: '150px'
    },
    {
      name: 'Action Type',
      selector: row => row.action_type,
      sortable: true,
      cell: row => (
        <span className={getActionTypeBadgeClass(row.action_type)}>
          {row.action_type}
        </span>
      ),
      width: '300px'
    },
    {
      name: 'Customer Name',
      selector: row => row.order?.user_name || 'N/A',
      sortable: true,
      width: '150px'
    },
    {
      name: 'Order Status',
      selector: row => row.order?.status || 'N/A',
      sortable: true,
      cell: row => (
        <span className={getStatusBadgeClass(row.order?.status)}>
          {row.order?.status ? row.order.status.charAt(0).toUpperCase() + row.order.status.slice(1) : 'N/A'}
        </span>
      ),
      width: '120px'
    },
    {
      name: 'Order Date',
      selector: row => row.order?.created_at || 'N/A',
      sortable: true,
      cell: row => (
        <span style={{ fontSize: '12px' }}>
          {row.order?.created_at ? new Date(row.order.created_at).toLocaleDateString('en-IN') : 'N/A'}
        </span>
      ),
      width: '120px'
    },
    {
      name: 'Actions',
      cell: row => (
        <Link 
          to={`/orders/${row.order_id}`}
          target={isMobile ? undefined : '_blank'}
          className="btn btn-primary"
          style={{ 
            fontSize: '12px', 
            padding: '6px 12px',
            textDecoration: 'none'
          }}
        >
          View Order
        </Link>
      ),
      width: '120px'
    }
  ];

  const getActionTypeBadgeClass = (actionType) => {
    const classes = {
      'Not shipped': 'status-badge status-pending',
      'Not Delivered (OTP verification pending)': 'status-badge status-shipped',
      'Invoice upload pending': 'status-badge status-cancelled',
      'DCR pending': 'status-badge status-cancelled'
    };
    return classes[actionType] || 'status-badge';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed':
        return 'status-badge status-confirmed';
      case 'pending':
        return 'status-badge status-pending';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'delivered':
        return 'status-badge status-delivered';
      default:
        return 'status-badge';
    }
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '32px',
      border: state.isFocused ? '1px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '12px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#ced4da'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontSize: '12px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0d6efd' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#212529',
      fontSize: '12px',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0d6efd' : '#f8f9fa'
      }
    })
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ marginBottom: '20px' }}>Pending Actions</h1>
          
          {/* Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Total Pending Actions</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#0d6efd' }}>
                {pendingActions.length}
              </div>
            </div>
            
            {summary.map((item, index) => (
              <div key={index} style={{ 
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.action_type}
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc3545' }}>
                  {item.count}
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className={isMobile ? `filters-card${!filtersOpen ? ' mobile-collapsed' : ''}` : undefined} style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr auto',
            gap: '12px',
            marginBottom: '20px',
            alignItems: 'end'
          }}>
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>
                  Filters{[filterText, selectedActionType, selectedWarehouse].filter(Boolean).length ? ` (${[filterText, selectedActionType, selectedWarehouse].filter(Boolean).length})` : ''}
                </span>
                <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
                  {filtersOpen ? 'Hide filters' : 'Filters'}
                </button>
              </div>
            )}
            <div>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Search by Order ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter order ID..."
                value={filterText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ fontSize: '12px', padding: '6px 8px' }}
              />
            </div>

            <div>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Filter by Action Type</label>
              <Select
                value={selectedActionType}
                onChange={handleActionTypeChange}
                options={actionTypes}
                isClearable
                isSearchable={!isMobile || actionTypes.length > 8}
                placeholder="Select action type..."
                menuPortalTarget={document.body}
                styles={{ ...selectStyles, menuPortal: b => ({ ...b, zIndex: 9999 }) }}
              />
            </div>

            <div>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Filter by Warehouse</label>
              <Select
                value={selectedWarehouse}
                onChange={handleWarehouseChange}
                options={warehouses}
                isClearable
                isSearchable={!isMobile || warehouses.length > 8}
                placeholder="Select warehouse..."
                menuPortalTarget={document.body}
                styles={{ ...selectStyles, menuPortal: b => ({ ...b, zIndex: 9999 }) }}
              />
            </div>

            <button
              onClick={handleClearFilters}
              className="btn btn-secondary"
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={pendingActions}
          progressPending={loading}
          progressComponent={<div style={{ padding: '20px' }}>Loading...</div>}
          noDataComponent={<div style={{ padding: '20px', textAlign: 'center' }}>No pending actions found</div>}
          striped
          highlightOnHover
          responsive
          customStyles={{
            headRow: {
              style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6'
              }
            },
            rows: {
              style: {
                borderBottom: '1px solid #dee2e6',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default PendingActions;
